import * as React from "react"
import { type Interval, type IntervalOrigin } from "@digits-graphql/frontend/graphql-bearer"
import { isAllTimeRouteParams } from "@digits-shared/helpers/allTimeHelpers"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import { computeAllTimeIntervalCount, createAllTimeOrigin } from "src/shared/allTime/allTimeHelpers"
import { useFirstTransactionTimestamp } from "src/shared/hooks/useFirstTransactionTimestamp"

/*
 INTERFACES
*/

interface AllTimeContextValue {
  /**
   * The interval used in all-time mode. undefined = not in all-time mode.
   * When set, indicates active all-time mode with the specified interval.
   */
  allTimeInterval: Interval | undefined
  /** The computed all-time origin for the current interval, if available */
  allTimeOrigin: IntervalOrigin | undefined
  /** Whether the all-time origin is still loading */
  loading: boolean
}

/*
 CONTEXT
*/

const AllTimeContext = React.createContext<AllTimeContextValue>({
  allTimeInterval: undefined,
  allTimeOrigin: undefined,
  loading: false,
})

/**
 * Hook to access all-time mode state.
 * Check `allTimeInterval !== undefined` to determine if in all-time mode.
 */
export function useAllTimeMode() {
  return React.useContext(AllTimeContext)
}

/*
 PROVIDER HOOKS
*/

interface UseAllTimeModeProviderOptions {
  /** Whether the feature is enabled (e.g., employee feature flag) */
  enabled: boolean
  /** Current URL params for detecting all-time URL pattern */
  urlParams: { year?: string; index?: string }
  /** Whether the current route supports time parameters */
  routeHasTimeOptions: boolean
  /** The interval to use for computing all-time origin */
  interval?: Interval
}

interface UseAllTimeModeFromOriginOptions {
  /** Whether the feature is enabled (e.g., employee feature flag) */
  enabled: boolean
  /** The current origin to check against all-time origin */
  origin: IntervalOrigin
}

/**
 * Hook that manages all-time mode state for the provider.
 * This encapsulates the state logic and returns values for the context.
 */
export function useAllTimeModeProvider(options: UseAllTimeModeProviderOptions) {
  const { enabled, urlParams, routeHasTimeOptions, interval } = options

  // Check if current URL is the all-time pattern
  const isAllTimeUrl = isAllTimeRouteParams(urlParams)

  // Only fetch firstTransactionTimestamp when feature is enabled AND on all-time URL
  const shouldFetch = enabled && isAllTimeUrl
  const { firstTransactionTimestamp, loading } = useFirstTransactionTimestamp(!shouldFetch)

  // Single state: the interval for all-time mode (undefined = not in all-time mode)
  const [allTimeInterval, setAllTimeInterval] = React.useState<Interval | undefined>(undefined)

  // Update state based on URL changes
  // - On all-time URL: capture the interval
  // - On regular URL with time tokens: clear (exit all-time mode)
  // - On route without time tokens: preserve (no change)
  React.useEffect(() => {
    if (!enabled) {
      setAllTimeInterval(undefined)
      return
    }

    // On all-time URL with time options - capture the interval
    if (isAllTimeUrl && routeHasTimeOptions && interval) {
      setAllTimeInterval(interval)
      return
    }

    // Regular route with time tokens (not all-time) - clear all-time mode
    // Routes without time tokens: don't update, preserving current value
    if (routeHasTimeOptions && !isAllTimeUrl) {
      setAllTimeInterval(undefined)
    }
  }, [enabled, isAllTimeUrl, routeHasTimeOptions, interval])

  // Compute all-time origin
  const allTimeOrigin = React.useMemo(() => {
    if (!enabled || !interval) return undefined
    return createAllTimeOrigin(interval, firstTransactionTimestamp)
  }, [enabled, interval, firstTransactionTimestamp])

  // Compute all-time interval count for URL resolution
  const allTimeIntervalCount = React.useMemo(() => {
    if (!enabled || !isAllTimeUrl || !firstTransactionTimestamp || !interval) {
      return undefined
    }
    return computeAllTimeIntervalCount(interval, firstTransactionTimestamp)
  }, [enabled, isAllTimeUrl, firstTransactionTimestamp, interval])

  const contextValue = React.useMemo<AllTimeContextValue>(
    () => ({
      allTimeInterval: enabled ? allTimeInterval : undefined,
      allTimeOrigin,
      loading,
    }),
    [enabled, allTimeInterval, allTimeOrigin, loading]
  )

  return {
    contextValue,
    allTimeIntervalCount,
    isAllTimeUrl,
  }
}

/**
 * Hook that computes all-time mode state from an origin (not URL-based).
 * Used by components like Inbox that manage their own local time state.
 * Only fetches firstTransactionTimestamp when feature is enabled.
 */
export function useAllTimeModeFromOrigin(options: UseAllTimeModeFromOriginOptions) {
  const { enabled, origin } = options
  const { firstTransactionTimestamp, loading } = useFirstTransactionTimestamp(!enabled)

  return React.useMemo<AllTimeContextValue>(() => {
    if (!enabled) {
      return { allTimeInterval: undefined, allTimeOrigin: undefined, loading: false }
    }

    const allTimeOrigin = createAllTimeOrigin(origin.interval, firstTransactionTimestamp)
    const isAllTimeOrigin = allTimeOrigin
      ? dateTimeHelper.areIntervalOriginsEqual(origin, allTimeOrigin)
      : false

    return {
      allTimeInterval: isAllTimeOrigin ? origin.interval : undefined,
      allTimeOrigin,
      loading,
    }
  }, [enabled, origin, firstTransactionTimestamp, loading])
}

/*
 PROVIDER COMPONENT
*/

export const AllTimeContextProvider = AllTimeContext.Provider
